import { getIdToken } from './auth';

export const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080';

/** Requests abort after this long so a hung endpoint cannot wedge the UI. */
const DEFAULT_TIMEOUT_MS = 30_000;

/**
 * Raised when the API rejects a request with 401. Distinct from a generic
 * failure so callers (and the global handler) can route the user to sign-in
 * rather than surfacing a raw "API ... failed: 401" string.
 */
export class AuthExpiredError extends Error {
  constructor() {
    super('Your session has expired. Please sign in again.');
    this.name = 'AuthExpiredError';
  }
}

/** Raised when a request exceeds its timeout and is aborted. */
export class ApiTimeoutError extends Error {
  constructor(method: string, path: string, timeoutMs: number) {
    super(`API ${method} ${path} timed out after ${timeoutMs}ms.`);
    this.name = 'ApiTimeoutError';
  }
}

/**
 * Raised for any non-2xx response other than 401 (which throws
 * AuthExpiredError instead). Carries the real status code so callers can
 * tell "the server said no such thing" (404) apart from "the server is
 * having a bad time" (5xx) or a network failure, rather than collapsing
 * every failure into the same generic message.
 */
export class ApiHttpError extends Error {
  readonly status: number;
  readonly statusText: string;
  constructor(method: string, path: string, status: number, statusText: string) {
    super(`API ${method} ${path} failed: ${status} ${statusText}`);
    this.name = 'ApiHttpError';
    this.status = status;
    this.statusText = statusText;
  }
}

let authExpiredHandler: (() => void) | null = null;

/**
 * Install a centralized handler invoked once per 401 response. The app wires
 * this at startup to route to the sign-in screen (see SiteApp), so an expired
 * session never dead-ends on a raw "API ... 401" string. When unset the
 * AuthExpiredError still surfaces for the caller to handle.
 */
export function setAuthExpiredHandler(handler: (() => void) | null): void {
  authExpiredHandler = handler;
}

function handleAuthExpired(): void {
  authExpiredHandler?.();
}

/**
 * `auth = false` skips `getIdToken()` entirely - for genuinely public
 * endpoints (`apiGetPublic`/`apiPostPublic` below) that must behave
 * identically for a signed-in creator and an anonymous visitor, and must
 * not attach the creator's own live Cognito ID token to a route that has no
 * use for it. This is not just belt-and-braces: `docs/editor-consolidation/
 * play-origin-isolation.md`'s own audit found the public play surface
 * (`playClient.ts`) had been calling the authenticated `apiGet`/`apiPost`
 * all along despite its own doc comment already claiming these endpoints
 * were unauthenticated - `playerSession.ts` (the relay's own player
 * identity) had already hand-rolled a `fetch` specifically to avoid this
 * exact thing, but `playClient.ts` itself had not caught up.
 */
async function getHeaders(contentType: string | null = 'application/json', auth = true): Promise<Record<string, string>> {
  const headers: Record<string, string> = {};
  if (auth) {
    const token = await getIdToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  }
  if (contentType) {
    headers['Content-Type'] = contentType;
  }
  return headers;
}

interface RequestOptions {
  body?: unknown;
  extraHeaders?: Record<string, string>;
  keepalive?: boolean;
  timeoutMs?: number;
  /** Defaults to true. Set false for a genuinely public endpoint - see
   * `getHeaders`'s own doc comment. */
  auth?: boolean;
}

/** Parse a JSON body, tolerating an empty response (e.g. 204 No Content). */
async function parseBody<T>(resp: Response): Promise<T> {
  if (resp.status === 204) return undefined as T;
  const text = await resp.text();
  if (!text) return undefined as T;
  return JSON.parse(text) as T;
}

async function request<T>(method: string, path: string, opts: RequestOptions = {}): Promise<T> {
  const auth = opts.auth ?? true;
  const headers = { ...(await getHeaders('application/json', auth)), ...opts.extraHeaders };
  const timeoutMs = opts.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  let resp: Response;
  try {
    resp = await fetch(`${API_URL}${path}`, {
      method,
      headers,
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
      keepalive: opts.keepalive ?? false,
      signal: controller.signal,
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new ApiTimeoutError(method, path, timeoutMs);
    }
    throw error;
  } finally {
    clearTimeout(timer);
  }

  if (resp.status === 401) {
    // A 401 on a call that never claimed to be an authenticated creator
    // session in the first place is not "your session expired" - routing an
    // anonymous play visitor to the creator sign-in screen for this would be
    // wrong. Still a real, surfaced error (ApiHttpError), just not this
    // special-cased one.
    if (auth) {
      handleAuthExpired();
      throw new AuthExpiredError();
    }
    throw new ApiHttpError(method, path, resp.status, resp.statusText);
  }
  if (!resp.ok) {
    throw new ApiHttpError(method, path, resp.status, resp.statusText);
  }
  return parseBody<T>(resp);
}

export async function apiGet<T = unknown>(path: string): Promise<T> {
  return request<T>('GET', path);
}

/** Like `apiGet`, but never attaches the signed-in creator's own session
 * token - see `getHeaders`'s own doc comment for why this exists. */
export async function apiGetPublic<T = unknown>(path: string): Promise<T> {
  return request<T>('GET', path, { auth: false });
}

export async function apiPost<T = unknown>(
  path: string,
  body?: unknown,
  extraHeaders?: Record<string, string>,
): Promise<T> {
  return request<T>('POST', path, { body, extraHeaders });
}

/** Like `apiPost`, but never attaches the signed-in creator's own session
 * token - for genuinely public endpoints (the contact form). See
 * `getHeaders`'s own doc comment for why this exists. */
export async function apiPostPublic<T = unknown>(
  path: string,
  body?: unknown,
  extraHeaders?: Record<string, string>,
): Promise<T> {
  return request<T>('POST', path, { body, extraHeaders, auth: false });
}

export async function apiPut<T = unknown>(
  path: string,
  body?: unknown,
  opts?: { keepalive?: boolean },
): Promise<T> {
  return request<T>('PUT', path, { body, keepalive: opts?.keepalive });
}

export async function apiPatch<T = unknown>(
  path: string,
  body?: unknown,
  extraHeaders?: Record<string, string>,
  opts?: { keepalive?: boolean },
): Promise<T> {
  return request<T>('PATCH', path, { body, extraHeaders, keepalive: opts?.keepalive });
}

export async function apiDelete<T = unknown>(path: string): Promise<T> {
  // Deletes/unpublishes routinely answer 204 with no body; parseBody treats an
  // empty response as success rather than throwing a JSON SyntaxError.
  return request<T>('DELETE', path);
}

export async function apiUpload(url: string, file: File, contentType: string): Promise<void> {
  const resp = await fetch(url, {
    method: 'PUT',
    headers: {
      'Content-Type': contentType,
    },
    body: file,
  });
  if (!resp.ok) {
    throw new Error(`File upload failed: ${resp.status} ${resp.statusText}`);
  }
}
